import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { type Swiper as SwiperType } from 'swiper'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import { useStories } from '@/api/models/stories'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { StoriesDrawer } from '@/components/entities/stories'
import { ButtonSkeleton, CardSkeleton } from '@/components/shared/skeleton'
import { APP_DRAWER_QUERY_KEY } from '@/utils/constants'

import { getPageRightOffset } from '../utils/getPageRightOffset'

export const StoriesSummary = () => {
  const searchParams = useSearchParams()

  const router = useRouter()
  const pathname = usePathname()

  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)
  const isLargeScreen = [ScreenSize.Xxl].includes(breakpoint)

  const { data: stories, isError, isLoading, refetch } = useStories()
  const swiperRef = useRef<SwiperType | null>(null)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  const isUrlActive =
    searchParams?.get(APP_DRAWER_QUERY_KEY) === 'stories' &&
    !!searchParams?.get('storyId')

  const [isOpen, setIsOpen] = useState(isUrlActive)

  const storiesLength = stories?.length ?? 0

  useEffect(() => {
    if (isUrlActive) {
      setIsOpen(true)
    }
  }, [isUrlActive])

  const closeDrawer = () => {
    refetch()
    setIsOpen(false)
  }

  const handleAfterLeave = () => {
    const params = new URLSearchParams(searchParams?.toString())
    params.delete('storyId')
    params.delete(APP_DRAWER_QUERY_KEY, 'stories')
    const queryString = params.toString()
    const url = queryString ? `${pathname}?${queryString}` : pathname ?? '/'
    router.push(url)
  }

  const onStoriesClick = (id: string) => {
    const params = new URLSearchParams(searchParams?.toString())
    params.set('drawer', 'stories')
    params.set('storyId', id)
    router.push(`${pathname}?${params}`)
  }

  const onSlidePrev = () => {
    swiperRef.current?.slidePrev()
    setCurrentSlideIndex(currentSlideIndex - 1)
  }

  const onSlideNext = () => {
    swiperRef.current?.slideNext()
    setCurrentSlideIndex(currentSlideIndex + 1)
  }

  if (isLoading) {
    return (
      <div className="no-scrollbar mb-32 flex gap-24 overflow-x-hidden rounded-16 bg-white px-24 py-20 xl:rounded-0 xl:bg-grey-10 xl:px-0 xl:py-0 sm:mb-16">
        {Array.from({ length: isLargeScreen ? 5 : 14 }).map((_, index) => (
          <div key={index} className="flex flex-col items-center gap-8 pb-24">
            <CardSkeleton className="h-80 w-80 shrink-0 rounded-24 xxs:h-68 xxs:w-68" />
            <ButtonSkeleton className="h-12 w-40" />
          </div>
        ))}
      </div>
    )
  }

  if (isError || stories?.length === 0) {
    return
  }

  return (
    <div className="relative mb-32 xl:mb-40 lg:mb-20 sm:mb-16">
      <SwiperComponent
        modules={[FreeMode, Mousewheel]}
        slidesPerView="auto"
        spaceBetween={24}
        slidesOffsetAfter={isLargeScreen ? 0 : getPageRightOffset(breakpoint)}
        mousewheel
        watchOverflow
        threshold={10}
        className="rounded-16 bg-grey-20 px-24 py-20 xl:rounded-0 xl:bg-grey-10 xl:px-0 xl:py-0"
        onInit={(swiper: SwiperType) => (swiperRef.current = swiper)}
      >
        {stories?.map((story, index) => (
          <SwiperSlide key={story.id} className="!w-auto">
            <button
              aria-label={`История №${index}`}
              className="flex max-w-[80px] flex-col gap-8 xxs:max-w-[68px]"
              onClick={() => onStoriesClick(story.id)}
            >
              <div
                className={cn(
                  'h-80 w-80 rounded-16 bg-gradient-to-br p-3 xxs:h-68 xxs:w-68',
                  story.isViewed
                    ? 'from-grey-30 to-grey-40'
                    : 'from-bronze-40/50 to-bronze-40'
                )}
              >
                <Image
                  className="h-full w-full rounded-13"
                  quality={100}
                  src={story.picture}
                  width={isMobileScreen ? 68 : 80}
                  height={isMobileScreen ? 68 : 80}
                  alt="story-image"
                />
              </div>
              <p className="line-clamp-2 hyphens-auto text-center text-14">
                {story.name}
              </p>
            </button>
          </SwiperSlide>
        ))}
      </SwiperComponent>

      <div
        className={cn(
          'absolute left-0 top-[60px] z-10 -translate-y-1/2 xl:hidden',
          currentSlideIndex === 0 ? 'hidden' : 'block'
        )}
      >
        <button
          className="h-24 w-24 rounded-8 bg-white p-4 shadow-md"
          aria-label="Назад"
          onClick={onSlidePrev}
        >
          <ChevronLeftIcon className="h-16 w-16" />
        </button>
      </div>
      <div
        className={cn(
          'absolute right-0 top-[60px] z-10 -translate-y-1/2 xl:hidden',
          storiesLength < 5 ? 'hidden' : 'block'
        )}
      >
        <button
          className="h-24 w-24 rounded-8 bg-white p-4 shadow-md"
          aria-label="Дальше"
          onClick={onSlideNext}
        >
          <ChevronRightIcon className="h-16 w-16" />
        </button>
      </div>
      <StoriesDrawer
        stories={stories ?? []}
        opened={isOpen}
        onClose={closeDrawer}
        onAfterLeave={handleAfterLeave}
      />
    </div>
  )
}
